import { akhirHariIso } from './actions.js';

/**
 * Aksi massal "Ubah tenggat" — UAT D-03.
 *
 * Yang dulu ada: dialog konfirmasi TANPA medan tanggal, dan `onConfirm` yang hanya
 * menutupnya. Nol permintaan jaringan, nol perubahan, nol galat, nol toast.
 *
 * Dua hal yang membuat berkas ini ada alih-alih beberapa baris di dalam komponen:
 *
 *   1. Ia dapat diuji. Yang perlu dibuktikan adalah "permintaannya benar-benar
 *      terkirim, satu per temuan, dengan tenggat yang diketik" — pernyataan
 *      tentang lalu lintas jaringan, bukan tentang piksel.
 *   2. Kegagalan SEBAGIAN punya tempat. Sebuah aksi atas lima temuan dapat
 *      berhasil pada empat, dan membulatkannya menjadi "berhasil" adalah cacat
 *      yang sama dengan D-03 dalam ukuran lebih kecil.
 *
 * Mengapa satu permintaan per temuan, bukan satu panggilan massal:
 * `POST /v1/findings/bulk/transitions` memang ada di manifest M02 dan di
 * `openapi.yaml`, tetapi TIDAK BERHANDLER — ia menjawab 404 (CATATAN-PENGEMBANG
 * §2.3 sekelasnya). Memakainya berarti menukar no-op senyap dengan galat; jadi
 * jalur yang dipakai adalah rute per-temuan yang benar-benar berjalan, dan
 * hasilnya dilaporkan per temuan.
 */

export interface BulkTransport {
  transition(code: string, body: unknown): Promise<unknown>;
}

export interface BulkResult {
  berhasil: string[];
  gagal: Array<{ code: string; problem: string }>;
}

/**
 * Penolakan sebelum satu permintaan pun dikirim, dibawa sebagai KODE.
 *
 * Bukan prosa: berkas ini tidak punya bahasa penggunanya, dan sebuah kalimat yang
 * lahir di sini akan muncul di layar dalam satu bahasa saja — bentuk D-07 yang
 * lebih kecil. Kodenya punya entri `error.*` di kedua katalog, dan layar yang
 * merendernya tahu bahasa pembacanya.
 */
export class BulkRefused extends Error {
  readonly problem: { code: string };
  constructor(code: string) {
    super(code);
    this.name = 'BulkRefused';
    this.problem = { code };
  }
}

export async function applyBulkDueDate(
  transport: BulkTransport,
  codes: readonly string[],
  dueDate: string,
  reason: string,
): Promise<BulkResult> {
  if (codes.length === 0) throw new BulkRefused('NOTHING_SELECTED');
  if (dueDate.trim() === '') throw new BulkRefused('DUE_DATE_IN_PAST');
  // `CHANGE_DUE_DATE` ber-`reason_required` di `workflows/finding.v1.json`.
  // Menemukannya di sini, sebelum satu permintaan pun dikirim, lebih baik daripada
  // n kali 422 yang identik.
  if (reason.trim() === '') throw new BulkRefused('REASON_REQUIRED');

  const body = {
    transition: 'CHANGE_DUE_DATE',
    reason: reason.trim(),
    input: { due_at: akhirHariIso(dueDate) },
  };

  const hasil: BulkResult = { berhasil: [], gagal: [] };
  for (const code of codes) {
    try {
      await transport.transition(code, body);
      hasil.berhasil.push(code);
    } catch (err) {
      // Satu temuan yang ditolak tidak menghentikan sisanya: manajer yang
      // memindahkan tenggat sepuluh temuan lebih butuh sembilan yang pindah plus
      // satu nama yang gagal daripada nol yang pindah.
      const problem =
        (err as { problem?: { code?: string } }).problem?.code ??
        (err instanceof Error ? err.message : 'INTERNAL');
      hasil.gagal.push({ code, problem });
    }
  }
  return hasil;
}
